// src/components/estados/EstadoFormDialog.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  ActorCancelacion,
  CreateEstadoReservaDto,
  EstadoCategoria,
  EstadoReserva,
  createEstado,
  updateEstado,
} from '../api/estadosReserva';

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved: (saved: EstadoReserva) => void;
  // si hay estado → edit; si no → create
  initial?: EstadoReserva | null;
};

const CATEGORIAS: EstadoCategoria[] = ['OK', 'CANCEL', 'PENDIENTE', 'BLOQUEO', 'INFO'];
const ACTORES: (ActorCancelacion | 'NONE')[] = ['ALUMNO', 'PROFESOR', 'SISTEMA', 'OTRO', 'NONE'];

/* Paleta rápida (ajústala a tus colores de marca si lo deseas) */
const QUICK_COLORS = [
  '#1e88e5',
  '#43a047',
  '#f4511e',
  '#8e24aa',
  '#3949ab',
  '#fb8c00',
  '#e53935',
  '#00acc1',
  '#5e35b1',
  '#607d8b',
  '#b39ddb',
];

function normalizeCode(v: string) {
  return v.trim().replace(/\s+/g, '_').toUpperCase();
}

// Validación simple (hex solamente, coherente con el input type="color")
function validate(form: Partial<CreateEstadoReservaDto>): string[] {
  const errors: string[] = [];
  if (!form.code || !/^[A-Z0-9_-]+$/.test(form.code)) {
    errors.push('code: usa MAYÚSCULAS, números, guion o guion_bajo');
  }
  if (!form.descripcion || String(form.descripcion).trim().length < 3) {
    errors.push('descripcion: mínimo 3 caracteres');
  }
  if (form.color && !/^#?[0-9a-fA-F]{3,8}$/.test(form.color)) {
    errors.push('color: usa hex (#RRGGBB) o déjalo vacío');
  }
  if (form.orden != null && isNaN(Number(form.orden))) {
    errors.push('orden: debe ser numérico');
  }
  return errors;
}

export default function EstadoFormDialog({ open, onClose, onSaved, initial }: Props) {
  const { t } = useTranslation<'translation'>();
  const isEdit = !!initial;

  const [code, setCode] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [categoria, setCategoria] = useState<EstadoCategoria>('OK');
  const [bloqueante, setBloqueante] = useState(false);
  const [cuentaHoras, setCuentaHoras] = useState(true);
  const [requiereComentario, setRequiereComentario] = useState(false);
  const [visibleCliente, setVisibleCliente] = useState(true);
  const [systemFlag, setSystemFlag] = useState(false);
  const [actorCancelacion, setActorCancelacion] = useState<ActorCancelacion | 'NONE'>('NONE');
  const [color, setColor] = useState<string>(''); // guardamos el valor elegido (o '' si ninguno)
  const [orden, setOrden] = useState<number>(100);

  useEffect(() => {
    if (open) {
      if (initial) {
        setCode(initial.code);
        setDescripcion(initial.descripcion);
        setCategoria(initial.categoria);
        setBloqueante(!!initial.bloqueante);
        setCuentaHoras(!!initial.cuentaHoras);
        setRequiereComentario(!!initial.requiereComentario);
        setVisibleCliente(!!initial.visibleCliente);
        setSystemFlag(!!initial.systemFlag);
        setActorCancelacion(initial.actorCancelacion ?? 'NONE');
        setColor(initial.color ?? '');
        setOrden(initial.orden ?? 100);
      } else {
        // defaults para crear
        setCode('');
        setDescripcion('');
        setCategoria('OK');
        setBloqueante(false);
        setCuentaHoras(true);
        setRequiereComentario(false);
        setVisibleCliente(true);
        setSystemFlag(false);
        setActorCancelacion('NONE');
        setColor('');
        setOrden(100);
      }
    }
  }, [open, initial]);

  const dto = useMemo<CreateEstadoReservaDto>(() => {
    return {
      code: normalizeCode(code),
      descripcion: descripcion?.trim(),
      categoria,
      bloqueante,
      cuentaHoras,
      requiereComentario,
      visibleCliente,
      color: color || null, // si no se elige nada, enviamos null
      orden: Number(orden ?? 100),
      actorCancelacion: actorCancelacion === 'NONE' ? null : actorCancelacion,
      systemFlag,
    };
  }, [
    code,
    descripcion,
    categoria,
    bloqueante,
    cuentaHoras,
    requiereComentario,
    visibleCliente,
    color,
    orden,
    actorCancelacion,
    systemFlag,
  ]);

  const [saving, setSaving] = useState(false);

  async function onSubmit() {
    const errs = validate(dto);
    if (errs.length) {
      errs.forEach((e) => toast.error(e));
      return;
    }
    setSaving(true);
    try {
      let saved: EstadoReserva;
      if (isEdit && initial) {
        // PATCH /:oldCode  (permitimos cambio de code)
        saved = await updateEstado(initial.code, dto);
        toast.success(t('estadoFormDialog.toast.updated'));
      } else {
        saved = await createEstado(dto);
        toast.success(t('estadoFormDialog.toast.created'));
      }
      onSaved(saved);
    } catch (e: any) {
      toast.error(e?.message || t('estadoFormDialog.toast.error'));
    } finally {
      setSaving(false);
    }
  }

  const previewColor = color || '#b39ddb';

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Editar estado' : 'Nuevo estado'}</DialogTitle>
      <DialogContent>
        <Box mt={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('estadoFormDialog.field.code')}
                fullWidth
                value={code}
                onChange={(e) => setCode(normalizeCode(e.target.value))}
                inputProps={{ maxLength: 64 }}
                helperText={t('estadoFormDialog.field.codeHelper')}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label={t('estadoFormDialog.field.descripcion')}
                fullWidth
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                inputProps={{ maxLength: 120 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="categoria-label">
                  {t('estadoFormDialog.field.categoria')}
                </InputLabel>
                <Select
                  labelId="categoria-label"
                  label={t('estadoFormDialog.field.categoria')}
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value as EstadoCategoria)}
                >
                  {CATEGORIAS.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="actor-label">
                  {t('estadoFormDialog.field.actorCancelacion')}
                </InputLabel>
                <Select
                  labelId="actor-label"
                  label={t('estadoFormDialog.field.actorCancelacion')}
                  value={actorCancelacion}
                  onChange={(e) => setActorCancelacion(e.target.value as any)}
                >
                  <MenuItem value="NONE">—</MenuItem>
                  {ACTORES.filter((a) => a !== 'NONE').map((a) => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label={t('estadoFormDialog.field.orden')}
                type="number"
                fullWidth
                value={orden}
                onChange={(e) => setOrden(Number(e.target.value))}
              />
            </Grid>

            {/* ====== Color: picker + swatches + preview ====== */}
            <Grid item xs={12} md={6}>
              <Stack spacing={1}>
                {/* Selector nativo con paleta */}
                <TextField
                  label={t('estadoFormDialog.field.color')}
                  type="color"
                  size="small"
                  value={previewColor} // siempre un valor válido para el input
                  onChange={(e) => setColor(e.target.value)} // guardamos hex con #
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170 }}
                />

                {/* Swatches de colores rápidos */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  {QUICK_COLORS.map((c) => (
                    <Tooltip title={c} key={c}>
                      <IconButton
                        size="small"
                        aria-label={`Elegir color ${c}`}
                        onClick={() => setColor(c)}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: c,
                          border: (t) => `1px solid ${t.palette.divider}`,
                          '&:hover': { opacity: 0.9 },
                          m: 0.25,
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>

                {/* Vista previa + texto */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      background: previewColor,
                    }}
                  />
                  <Typography variant="caption">{color || '—'}</Typography>
                  {!!color && (
                    <Button size="small" onClick={() => setColor('')} sx={{ ml: 1 }}>
                      {t('estadoFormDialog.field.limpiar')}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Grid>
            {/* ================================================ */}

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={3} sx={{ mt: { xs: 1, md: 0 } }} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={bloqueante}
                      onChange={(e) => setBloqueante(e.target.checked)}
                    />
                  }
                  label={t('estadoFormDialog.field.bloqueante')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={cuentaHoras}
                      onChange={(e) => setCuentaHoras(e.target.checked)}
                    />
                  }
                  label={t('estadoFormDialog.field.cuentaHoras')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={requiereComentario}
                      onChange={(e) => setRequiereComentario(e.target.checked)}
                    />
                  }
                  label={t('estadoFormDialog.field.requiereComentario')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={visibleCliente}
                      onChange={(e) => setVisibleCliente(e.target.checked)}
                    />
                  }
                  label={t('estadoFormDialog.field.visibleCliente')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={systemFlag}
                      onChange={(e) => setSystemFlag(e.target.checked)}
                    />
                  }
                  label={t('estadoFormDialog.field.interno')}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button onClick={onSubmit} variant="contained" disabled={saving}>
          {isEdit ? t('estadoFormDialog.button.save') : t('estadoFormDialog.button.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
